import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addTimeEntry, updateTimeEntry, deleteTimeEntry } from '../api/workItem';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';
import { toast } from 'react-hot-toast';

import { useTaskDetailsQuery } from './workItem';

/**
 * useWorkLogs - Pulls the time entry list from the task details query cache.
 * Wrapping useTaskDetailsQuery ensures we perfectly share the same cache and network requests.
 */
export const useWorkLogs = (projectUuid?: string, workItemUuid?: string) => {
  const { data, isLoading, isFetching } = useTaskDetailsQuery(projectUuid, workItemUuid, 0, 0, false);
  
  return {
    data: data?.workItemTimeEntryDTOList || [],
    isLoading,
    isFetching
  };
};

export const useCreateWorkLog = () => {
  const queryClient = useQueryClient();
  const authUser = useAppSelector(selectAuthUser);

  return useMutation({
    mutationFn: addTimeEntry,

    onMutate: async (newLog) => {
      const queryKeyPrefix = ['task', newLog.projectUuid, newLog.workItemUuid];
      
      // 1. Cancel outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: queryKeyPrefix });

      // 2. Snapshot the previous data using getQueriesData to catch the exact key
      const previousQueries = queryClient.getQueriesData({ queryKey: queryKeyPrefix });

      // 3. Optimistically update the cache using setQueriesData
      queryClient.setQueriesData({ queryKey: queryKeyPrefix }, (oldTask: any) => {
        if (!oldTask) return oldTask;

        const fakeLog = {
          timeEntryUuid: `temp-${Date.now()}`, // Temporary ID
          workItemUuid: newLog.workItemUuid,
          dateStarted: newLog.dateStarted,
          timeSpent: newLog.timeSpent, // e.g. "2h 30m"
          description: newLog.description,
          loggedByAppUserFullName: authUser?.firstName ? `${authUser.firstName} ${authUser.lastName || ''}`.trim() : 'You',
          loggedByAppUserUuid: authUser?.appUserUuid || 'current-user',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isEdited: false,
          status: true,
        };

        return {
          ...oldTask,
          workItemTimeEntryDTOList: [
            fakeLog,
            ...(oldTask.workItemTimeEntryDTOList || []),
          ],
        };
      });

      return { previousQueries, queryKeyPrefix };
    },

    onError: (err, newLog, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    onSuccess: (data) => {
      const status = data?.status ?? data?.response?.status;
      if (status === false) {
        toast.error(data?.message ?? data?.response?.message ?? 'Failed to log time');
        return;
      }
      toast.success('Time logged successfully');
    },

    onSettled: (data, error, variables, context) => {
      if (context?.queryKeyPrefix) {
        queryClient.invalidateQueries({ queryKey: context.queryKeyPrefix });
      }
    },
  });
};

export const useUpdateWorkLog = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTimeEntry,
    onSuccess: (data, variables) => {
      const status = data?.status ?? data?.response?.status;
      if (status === false) {
        toast.error(data?.message ?? data?.response?.message ?? 'Failed to update time entry');
        return;
      }
      toast.success('Time entry updated');
      queryClient.invalidateQueries({
        queryKey: ['task', variables.projectUuid, variables.workItemUuid],
      });
    },
  });
};

export const useDeleteWorkLog = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTimeEntry,
    onSuccess: (_, variables) => {
      toast.success('Time entry deleted');
      queryClient.invalidateQueries({
        queryKey: ['task', variables.projectUuid, variables.workItemUuid],
      });
    },
  });
};
